import React, { useEffect, useMemo, useState, useRef } from 'react'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { captureQuizSubmitted } from './quizAnalytics'
import { AppUseDispatch, AppUseSelector } from '../../redux/hooks'
import {
  getStory,
  setStep,
  saveLastStep,
  wordDoesNotKnow,
  wordDoKnow,
  getWordDetail,
} from '../../redux/actions/storyAction'
import { getQuiz, submitQuiz, getGeneralResult } from '../../redux/actions/quizAction'
import { storyStep, MemberStepInStory } from '../../utils/constant'
import QuizAnswer from './QuizAnswer'
import QuizResult from './QuizResult'
import Loader from '../../containers/loader/loader'
import NavigationFooter from '../../containers/navigation-footer/NavigationFooter'
import { QUESTION, STORY } from 'routes/routeConstant'
import OneLineContent from '../../containers/RenderStroyComponent/OneLineContent'
import { AddWordToHackChinese, handleAddWordToSkritter } from '../../redux/actions/userAction'
import WithRedirect from 'containers/WithRedirect'
import { REGEX } from 'utils/regex'
import { converterWord, getWordLine, getWordTypeFromStory, wordSimplified, getSentenceWords } from 'utils/helperFunction'
import { useNavigate, useParams } from 'react-router-dom'
import { CHECK_WORD_IS_CLICKED, RESET_QUIZ } from '../../redux/types'
import WordDetail from 'containers/word-detail'
import WordExamplesModal from 'containers/modal/word-examples-modal'
import StoryContent from 'containers/RenderStroyComponent/StoryContent'

const activeTabConst = {
  quiz: 1,
  story: 2,
}
const activeStateConst = {
  loading: '-1',
  quiz: '1',
  answer: '2',
  result: '3',
  story: '4',
}
// The two tabs; labels are translation keys.
const TABS = [
  { tab: activeTabConst.quiz, label: 'quiz:tabs.quiz' },
  { tab: activeTabConst.story, label: 'quiz:tabs.story' },
]

/** The story's words, or none while the story has not loaded. */
export const storyWordsOf = (story: any): any[] => (Array.isArray(story?.storyContent) ? [...story.storyContent] : [])

/** True when the store does not hold the story the URL names. */
export const needsStoryLoad = (story: any, storyId: string): boolean => !!storyId && story?.id !== storyId

const selectedIcon = <i className='fa fa-check-circle color-primary' />
const unSelectedIcon = <i className='fa fa-circle-o' />

const Quiz: React.FC<any> = ({
  quizDetail = {},
  story,
  storyLevel,
  quizAnswer,
  quizResult,
  isExistsAnswer,
  lastStepEnum,
  credentials,
  user_auth_token,
  skritter_token,
}) => {
  const { memberId } = credentials
  const { t } = useTranslation('quiz')
  const { id: storyId = '' } = useParams()
  const navigate = useNavigate()
  const dispatch = AppUseDispatch()
  const optionsContent = quizDetail?.optionsWithContent?.map((optionsContent) => optionsContent?.opContect)?.flat()
  const { chineseWordFontSize } = AppUseSelector(({ dashboard }) => dashboard)
  const characterPreference = credentials?.customFields?.character
  const { wordDetails, isWordClick } = AppUseSelector(({ story }) => story)
  const [selected, setSelected] = useState({ value: false, label: '' })
  const [activeTab, setActiveTab] = useState(activeTabConst.quiz)
  const extraDivRef = useRef<HTMLDivElement>(null)
  const contentWordRef = useRef<HTMLDivElement>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [gettingResult, setGettingResult] = useState(false)
  const [gettingQuiz, setGettingQuiz] = useState(false)
  const [activeIndex, setActiveIndex] = useState('-1')
  const [quizWord, setQuizWord] = useState<any>(() => [...(quizDetail?.quizContent || []), ...(optionsContent || [])])
  // Opened straight from a link or a reload, the store has no story yet: start empty, never throw.
  const [storyWord, setStoryWord] = useState<any>(() => storyWordsOf(story))
  const [wordDetailVisible, setWordDetailVisible] = useState(false)
  const [openExampleModal, setOpenExampleModal] = useState(false)
  const [isImportanceOpen, setImportanceOpen] = useState(false)
  const [isMasteryOpen, setMasteryOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const audio = useMemo(() => story?.audio, [story])

  const setTab = (tab = activeTabConst.quiz) => setActiveTab(tab)
  const onSelectQuiz = (option) => setSelected(option)

  useEffect(() => {
    // Wait for this page's story: the quiz of another stored story must not show here.
    if (storyId && storyLevel && story?.id === storyId) {
      if (lastStepEnum !== null && lastStepEnum < storyStep.Step2) {
        saveLastStep(MemberStepInStory.QUIZ, storyLevel, storyId)
      }
      if (!quizDetail?.quiz) {
        setGettingQuiz(true)
        getQuiz(storyId, storyLevel)
          .then((result: any) => {
            const optionsContent = result?.optionsWithContent?.map((option) => option?.opContect).flat()
            setQuizWord([...(result?.quizContent || []), ...(optionsContent || [])])
            if (result.memberAnswer) {
              getResult()
            }
          })
          .catch((error) => {
            console.error('Error getting quiz:', error)
          })
          .finally(() => {
            setGettingQuiz(false)
          })
      }
    }
  }, [storyId, storyLevel, story?.id])

  useEffect(() => {
    window.scrollTo(0, 0)
    setStep(storyStep.Step2)

    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Load the story the URL names when the store has none (a link or a reload) or holds another
  // one. getStory resolves a story from its id alone. A story that cannot be loaded sends the
  // learner to the reader, which says so.
  const storyRequested = useRef<string | null>(null)
  useEffect(() => {
    if (!needsStoryLoad(story, storyId) || storyRequested.current === storyId) return
    storyRequested.current = storyId
    const otherStory = !!story?.id
    if (otherStory) dispatch({ type: RESET_QUIZ })
    getStory(storyId, otherStory ? '' : storyLevel).catch(() => navigate(`${STORY}/${storyId}`, { replace: true }))
  }, [storyId, story?.id])

  useEffect(() => {
    setStoryWord(storyWordsOf(story))
  }, [story?.id])

  const handleClickOutside = (event) => {
    const isInsideSpecificButtons = event.target.closest('[data-specific-buttons]')
    if (
      !isInsideSpecificButtons &&
      !contentWordRef.current?.contains(event.target) &&
      !extraDivRef.current?.contains(event.target)
    ) {
      if (wordDetailVisible && wordDetails?.word && !isWordClick) {
        const mastery = getWordTypeFromStory(wordDetails?.word, story, characterPreference)
        wordDoesNotKnow({
          word: wordSimplified(wordDetails?.word),
          storyId,
          mastery: mastery === 'word' ? undefined : mastery,
          page: 'story',
        })
        dispatch({ type: CHECK_WORD_IS_CLICKED, payload: false })
      }
      setWordDetailVisible(false)
      setImportanceOpen(false)
      setMasteryOpen(false)
      setActiveIndex('-1')
    } else if (isInsideSpecificButtons) {
      const mastery = getWordTypeFromStory(wordDetails?.word, story, characterPreference)
      if (wordDetailVisible && wordDetails?.word && !isWordClick) {
        wordDoesNotKnow({
          word: wordSimplified(wordDetails?.word),
          storyId,
          mastery: mastery === 'word' ? undefined : mastery,
          page: 'story',
        })
        dispatch({ type: CHECK_WORD_IS_CLICKED, payload: false })
      }
    }
  }

  useEffect(() => {
    if (openExampleModal) {
      return
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [extraDivRef, contentWordRef, openExampleModal, wordDetailVisible, isWordClick, wordDetails])

  const onSubmit = () => {
    if (!isSubmitting) {
      setIsSubmitting(true)
      submitQuiz(storyId, storyLevel, selected.value as unknown as string)
        .then((answer) => {
          setIsSubmitting(false)
          captureQuizSubmitted(storyId, storyLevel, answer)
        })
        .catch(() => {
          setIsSubmitting(false)
        })
    }
  }
  const getResult = () => {
    setGettingResult(true)
    const handleDone = () => setGettingResult(false)
    getGeneralResult(storyId).then(handleDone).catch(handleDone)
  }
  let activeState = activeStateConst.loading
  if (activeTab === activeTabConst.story) {
    activeState = activeStateConst.story
  } else if (gettingQuiz) {
    activeState = activeStateConst.loading
  } else if (isExistsAnswer) {
    if (quizResult) {
      activeState = activeStateConst.result
    } else if (gettingResult) {
      activeState = activeStateConst.loading
    } else {
      activeState = activeStateConst.loading
    }
  } else if (quizDetail) {
    if (quizResult) {
      activeState = activeStateConst.result
    } else if (quizAnswer && quizAnswer.selectedAnswer && quizAnswer.rightAnswer) {
      activeState = activeStateConst.answer
    } else {
      activeState = activeStateConst.quiz
    }
  }

  const onNext = () => {
    const uniqueItems = {}
    quizWord.forEach((item) => {
      uniqueItems[item.word] = item
    })
    const wordList = Object.values(uniqueItems)
      ?.filter((wordInfo: any) => !REGEX.specialChars.test(wordInfo?.word))
      .map((wordInfo: any) => wordSimplified(wordInfo?.word))
    wordDoKnow({ wordList, storyId })
    navigate(`${QUESTION}/${storyId}`)
  }

  const handle = {
    onClickWord: (event, word, sentence, key, storySection) => {
      if (isMobile) {
        const targetElement = document.getElementById(key)
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth' })
        }
      }
      if (typeof word !== 'string' && !REGEX.specialChars.test(word?.word)) {
        const quizWords = (storySection ? storyWord : quizWord)
          .filter?.((wordInfo) => {
            if (
              wordInfo?.word.includes('<p>') ||
              wordInfo?.word.includes('</p>') ||
              wordInfo?.word.includes('</p><p>')
            ) {
            } else {
              return wordInfo
            }
          })
          ?.filter((wordInfo: any) => wordInfo?.word !== word?.word)
        if (storySection) {
          setStoryWord(quizWords)
        } else {
          setQuizWord(quizWords)
        }
        handle.translateWord(event, sentence, storySection)
      }
      setActiveIndex(activeIndex === key ? `-1` : key)
      event.preventDefault()
      event.stopPropagation()
    },
    translateWord: (event: any, sentence: any, storySection: boolean) => {
      if (
        (event.type === 'mouseover' || event.type === 'click') &&
        event.target.innerText &&
        event.target.innerText.length > 0
      ) {
        const word = event.target.innerText
        if (/^[&.-”“，、；？@!#$%^&*()]+$/.test(word) || word.trim() === '。') {
          return
        }
        const wordTranslate = (storySection ? story.storyContent : quizDetail?.allquizopContent[0])?.filter(
          (iword) => converterWord(iword?.word, characterPreference) === word,
        )

        if (wordTranslate && wordTranslate.length > 0) {
          handle.getWordDetail(word, sentence, wordTranslate[0])
        }
      }
    },
    getWordDetail: async (word: string, sentence: any, data: any) => {
      const sentenceString = getWordLine(sentence)
      const basicDetail = {
        word,
        pinyin: data?.pinyin,
        english: data?.english,
        sentenceString,
        sentenceWords: getSentenceWords(sentence),
      }

      if (word && memberId) {
        setWordDetailVisible(true)
        const mastery = getWordTypeFromStory(word, story, characterPreference)
        await getWordDetail({
          detail: basicDetail,
          payload: { word: wordSimplified(word), memberId },
          wordType: mastery,
        }).then((res) => {
          handle.onWordClickIntegration({ word: res?.word, pinyin: res?.pinyin, english: res?.english })
        })
      }
    },
    onWordClickIntegration: (obj: any) => {
      if (user_auth_token) {
        AddWordToHackChinese({
          word: obj?.word,
          pinyin: obj?.pinyin,
          user_auth_token: user_auth_token,
        })
      }
      if (skritter_token) {
        handleAddWordToSkritter({
          word: obj?.word,
          translate: { pinyin: obj?.pinyin, english: obj?.english },
          title: story?.storyTitleContent,
          skritter_token: skritter_token,
        })
      }
    },
  }

  return (
    <>
      <div className='section-2'>
        <div className='section-story w-container'>
          {story && (
            <>
              <div className='w-nav story-nav-content quiz-tabs-main-div'>
                {TABS.map(({ tab, label }) => (
                  <a
                    key={tab}
                    className={`link-block ${activeTab === tab ? 'active' : ''}`}
                    onClick={() => setTab(tab)}
                  >
                    {t(label)}
                  </a>
                ))}
              </div>
              {activeState === activeStateConst.loading && <Loader />}
              {activeState === activeStateConst.quiz && (
                <div className='w-row section-story-content'>
                  <div className='w-col w-col-9 content-left'>
                    <div className='session-title'>
                      <div className='word_wrapper'>
                        {quizDetail?.quizContent?.map((wordObj, index) => (
                          <OneLineContent
                            key={index}
                            WIndex={index}
                            iterator={'QUIZ-QUESTION-TITLE'}
                            isFontBold={true}
                            chineseWordFontSize={chineseWordFontSize}
                            wordObj={wordObj}
                            contentDetail={quizDetail?.quizContent || []}
                            story={story}
                            showPinYin={true}
                            activeIndex={activeIndex}
                            handleClickWord={handle.onClickWord}
                          />
                        ))}
                      </div>
                    </div>

                    <div className='quiz-answers'>
                      <div className='form-quiz-answers'>
                        {quizDetail.options?.map((option, ind) => (
                          <div className='form-item m-pointer m-flx m-al-c' key={ind}>
                            <div className='m-radio' onClick={() => onSelectQuiz(option)}>
                              {selected.value === option.value ? selectedIcon : unSelectedIcon}
                            </div>
                            <span
                              style={{
                                fontSize: chineseWordFontSize?.newWordsExample,
                                lineHeight: '1.5',
                                letterSpacing: '0px',
                              }}
                              onClick={() => onSelectQuiz(option)}
                            >
                              <div className='word_wrapper'>
                                {quizDetail.optionsWithContent[ind]?.opContect?.map((wordObj, index) => (
                                  <OneLineContent
                                    key={index}
                                    WIndex={index}
                                    iterator={`QUIZ-OPTION-${ind}`}
                                    wordObj={wordObj}
                                    contentDetail={quizDetail?.optionsWithContent[ind]?.opContect || []}
                                    story={story}
                                    showPinYin={true}
                                    activeIndex={activeIndex}
                                    handleClickWord={handle.onClickWord}
                                  />
                                ))}
                              </div>
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                    {selected.value && (
                      <div className='quiz-button-answer'>
                        <div className='disable-correction-container new-tooltip' tabIndex={1}>
                          <button
                            className='button-2 w-button green-btn-class'
                            disabled={isSubmitting}
                            onClick={onSubmit}
                          >
                            {isSubmitting ? <Loader showText={false} /> : t('submit')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {activeState === activeStateConst.answer && (
                <QuizAnswer loading={gettingResult} result={quizAnswer} onClick={getResult} />
              )}
              {activeState === activeStateConst.result && (
                <QuizResult
                  user_auth_token={user_auth_token}
                  skritter_token={skritter_token}
                  quizResult={quizResult}
                  quizDetail={quizDetail}
                  selected={quizAnswer}
                  optionsContent={optionsContent}
                  story={story}
                  handleClickWord={handle.onClickWord}
                  storyId={storyId}
                  activeIndex={activeIndex}
                  chineseWordFontSize={chineseWordFontSize}
                />
              )}
              {activeState === activeStateConst.story && (
                <div className='w-row section-story-content section-story'>
                  <div className='w-col w-col-small-12 content-left'>
                    <div
                      style={{
                        fontSize: chineseWordFontSize?.storyContent,
                        lineHeight: '1.5',
                        letterSpacing: '0px',
                      }}
                      className='quiz-story story-content'
                    >
                      <StoryContent
                        story={story}
                        showTranslate={false}
                        handleClickWord={(event, word, sentence, key) =>
                          handle.onClickWord(event, word, sentence, key, true)
                        }
                        showPinYin={true}
                        activeIndex={activeIndex}
                        chineseWordFontSize={chineseWordFontSize}
                      />
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <NavigationFooter
        className='right-active'
        audio={audio}
        backPath={`${STORY}/${storyId}`}
        nextText={t('nextStep')}
        nextPath={`${QUESTION}/${storyId}`}
        nextOnclick={onNext}
      />
      <WordDetail
        ref={contentWordRef}
        setOpenExampleModal={setOpenExampleModal}
        isImportanceOpen={isImportanceOpen}
        setImportanceOpen={setImportanceOpen}
        isMasteryOpen={isMasteryOpen}
        setMasteryOpen={setMasteryOpen}
        setWordDetailVisible={setWordDetailVisible}
        setActiveIndex={setActiveIndex}
        visible={wordDetailVisible}
        openExampleModal={openExampleModal}
        showMasterySection={true}
      />
      <WordExamplesModal
        ref={extraDivRef}
        openExampleModal={openExampleModal}
        setOpenExampleModal={setOpenExampleModal}
        setWordDetailVisible={setWordDetailVisible}
      />
    </>
  )
}

const mapStateToProps = (state: any) => ({
  user_auth_token: state.user.user_auth_token,
  skritter_token: state.user.skritter_token,
  credentials: state.user.credentials,
  quizDetail: state.quiz.quizDetail,
  isExistsAnswer: state.quiz.isExistsAnswer,
  quizAnswer: state.quiz.quizAnswer,
  quizResult: state.quiz.quizResult,
  story: state.story.currentStory,
  storyLevel: state.story.storyLevel,
  errors: state.UI.errors,
  lastStepEnum: state.story.lastStepEnum,
  needGetData:
    // (state.user.credentials.level.length > 0 || state.user.credentials.customFields?.level.length > 0) &&
    !state.story.errors && (JSON.stringify(state.story.currentStory) === '{}' || state.story.currentStory.id),
  question: state.question,
})
export default connect(mapStateToProps)(WithRedirect(Quiz))
